/**
 * Stammplatz, Vollstaendigkeit und „Alles da“ (ADR 0013 §6, §8, §9; ADR 0015).
 *
 * Der Stammplatz sagt, wo etwas **hingehoert** -- der Zustand, wo es **ist**.
 * Aus dem Unterschied folgt alles hier: was fehlt, was fremd ist, und was
 * „Alles da“ zurueckbucht.
 *
 * **Der Soll-Baum.** Feste Orte haengen, wie in den Stammdaten angelegt
 * (`eltern_id`). Ein Packstueck haengt an seinem Stammplatz, und ohne
 * Stammplatz dort, wo es gerade gebucht ist (so vom Nutzer entschieden,
 * ADR 0015). „Alles da“ am LKW erreicht damit auch die Kisten, die noch an der
 * Einsatzstelle stehen -- und deren Inhalt.
 *
 * Rein und ohne Datenbank, wie alles in `@thw/core-domain`: Das Geraet rechnet
 * damit offline, der Server prueft damit beim Setzen, dass kein Kreis entsteht.
 */
import { MAX_PACKSTUECK_TIEFE } from "./packstueck.js";
import { uebergeordneterOrt, verschiebenNach, zielVerboten, } from "./verschieben.js";
/** Stammdaten koennen tiefer sein als Kisten in Kisten -- Halle, Regal, Fach. */
const KETTEN_GRENZE = MAX_PACKSTUECK_TIEFE * 3;
/**
 * Ist der Artikel an seinem Stammplatz?
 *
 * Wer in seiner Kiste steckt, ist da -- auch wenn die Kiste gerade im Einsatz
 * ist. Er kommt mit ihr zurueck (ADR 0012).
 */
export function amStammplatz(zustand, stammplatz_id) {
    return (zustand !== undefined &&
        zustand.verbleib !== "nicht_erfasst" &&
        zustand.lagerort_id === stammplatz_id);
}
/**
 * Wo ein Lagerort im Soll-Baum haengt.
 *
 * Ein Packstueck mit Stammplatz an seinem Stammplatz, eines ohne dort, wo es
 * gerade gebucht ist. Feste Orte nach den Stammdaten.
 */
export function sollOberort(ort, welt) {
    if (ort.artikel_id !== null) {
        const stammplatz = welt.stammplaetze.get(ort.artikel_id) ?? null;
        if (stammplatz !== null)
            return stammplatz;
    }
    return uebergeordneterOrt(ort, (id) => welt.zustaende.get(id));
}
/**
 * Der Weg im Soll-Baum nach oben: der Ort selbst, dann wo er hingehoert, und
 * so weiter. Bricht bei einem Kreis und nach einer festen Tiefe ab.
 */
export function sollKette(start, welt) {
    const kette = [];
    let aktuell = start;
    while (aktuell !== null && !kette.includes(aktuell) && kette.length < KETTEN_GRENZE) {
        kette.push(aktuell);
        const ort = welt.orte.get(aktuell);
        aktuell = ort === undefined ? null : sollOberort(ort, welt);
    }
    return kette;
}
/** Alle Lagerorte, die im Soll-Baum unter `wurzel` haengen -- sie selbst eingeschlossen. */
export function sollTeilbaum(wurzel, welt) {
    const teilbaum = new Set();
    for (const id of welt.orte.keys()) {
        if (sollKette(id, welt).includes(wurzel))
            teilbaum.add(id);
    }
    return teilbaum;
}
/** Der Lagerort, der dieser Artikel ist -- bei Packstuecken, sonst `null`. */
function ortDesArtikels(orte, artikel_id) {
    for (const o of orte.values()) {
        if (o.artikel_id === artikel_id)
            return o.id;
    }
    return null;
}
/**
 * Ergaeben diese Stammplaetze einen Kreis?
 *
 * Ein Packstueck, dessen Stammplatz in ihm selbst liegt -- direkt oder ueber
 * ein Unter-Packstueck --, haette keinen Platz im Soll-Baum, und „Alles da“
 * wuesste nicht, was zuerst kommt. Geprueft wird mit allen neuen Stammplaetzen
 * zugleich, so wie sie nach dem Setzen gelten. Liefert die Artikel, bei denen
 * es sich im Kreis dreht.
 */
export function stammplatzKreise(neu, welt) {
    const stammplaetze = new Map(welt.stammplaetze);
    for (const [artikel_id, stammplatz] of neu)
        stammplaetze.set(artikel_id, stammplatz);
    const danach = { ...welt, stammplaetze };
    const kreise = [];
    for (const [artikel_id, stammplatz] of neu) {
        if (stammplatz === null)
            continue;
        const eigen = ortDesArtikels(welt.orte, artikel_id);
        if (eigen !== null && sollKette(stammplatz, danach).includes(eigen))
            kreise.push(artikel_id);
    }
    return kreise;
}
/** Soll · da · fehlt · fremd an einem Lagerort (ADR 0013 §9). */
export function vollstaendigkeit(ort_id, welt) {
    const darin = sollTeilbaum(ort_id, welt);
    darin.delete(ort_id);
    let soll = 0;
    let da = 0;
    let verliehen = 0;
    let fehltDarin = 0;
    const fehlend = [];
    for (const [artikel_id, stammplatz] of welt.stammplaetze) {
        if (stammplatz === null)
            continue;
        const z = welt.zustaende.get(artikel_id);
        if (z?.archiviert === true)
            continue;
        const hier = amStammplatz(z, stammplatz);
        if (stammplatz === ort_id) {
            soll += 1;
            if (hier) {
                da += 1;
            }
            else {
                fehlend.push(artikel_id);
                if (z?.verbleib === "ausgeliehen")
                    verliehen += 1;
            }
        }
        else if (!hier && darin.has(stammplatz)) {
            fehltDarin += 1;
        }
    }
    const fremd = [];
    for (const [artikel_id, z] of welt.zustaende) {
        if (z.lagerort_id !== ort_id || z.archiviert || z.verbleib === "nicht_erfasst")
            continue;
        const stammplatz = welt.stammplaetze.get(artikel_id) ?? null;
        if (stammplatz !== null && stammplatz !== ort_id)
            fremd.push(artikel_id);
    }
    return { soll, da, fehlend, verliehen, fremd, fehltDarin };
}
/**
 * Was „Alles da“ an diesem Lagerort buchen wuerde (ADR 0013 §8).
 *
 * Alles, dessen Stammplatz im Soll-Baum unter `wurzel` liegt und das nicht
 * dort ist -- verschachtelt: Die Kisten kommen zurueck auf den LKW, ihr Inhalt
 * zurueck in die Kisten. Was in einer Kiste steckt, die zurueckkommt, kommt
 * mit ihr und braucht keine eigene Zeile. Der Status bleibt, wie er ist.
 *
 * Was einen Status mit `rueckbuchungIgnorieren` hat, bleibt liegen und steht
 * in `liegenLassen` (ADR 0025): Ein defektes Geraet soll nicht zurueck in die
 * Kiste, nur weil jemand „Alles da“ drueckt.
 */
export function allesDaPlan(wurzel, welt) {
    const teilbaum = sollTeilbaum(wurzel, welt);
    const zeilen = [];
    const verliehen = [];
    const archiviert = [];
    const liegen = [];
    for (const [artikel_id, stammplatz] of welt.stammplaetze) {
        if (stammplatz === null || !teilbaum.has(stammplatz))
            continue;
        const z = welt.zustaende.get(artikel_id);
        if (z?.archiviert === true) {
            archiviert.push(artikel_id);
            continue;
        }
        if (amStammplatz(z, stammplatz))
            continue;
        if (z?.verbleib === "ausgeliehen") {
            verliehen.push(artikel_id);
            continue;
        }
        if (z?.status !== undefined && welt.rueckbuchungIgnorieren?.has(z.status) === true) {
            liegen.push({ artikel_id, stammplatz });
            continue;
        }
        const kette = sollKette(stammplatz, welt);
        zeilen.push({
            artikel_id,
            stammplatz_id: stammplatz,
            von: z,
            tiefe: kette.indexOf(wurzel),
            haengt_an: null,
            kette,
        });
    }
    // Woran haengt eine Zeile? An der naechsten Kiste auf dem Weg nach oben,
    // die selbst bewegt wird.
    const bewegt = new Set(zeilen.map((z) => z.artikel_id));
    for (const zeile of zeilen) {
        for (const ort_id of zeile.kette) {
            if (ort_id === wurzel)
                break;
            const kiste = welt.orte.get(ort_id)?.artikel_id ?? null;
            if (kiste !== null && kiste !== zeile.artikel_id && bewegt.has(kiste)) {
                zeile.haengt_an = kiste;
                break;
            }
        }
    }
    zeilen.sort((a, b) => a.tiefe - b.tiefe ||
        (a.stammplatz_id < b.stammplatz_id ? -1 : a.stammplatz_id > b.stammplatz_id ? 1 : 0) ||
        (a.artikel_id < b.artikel_id ? -1 : 1));
    const fremd = [];
    const ohneStammplatz = [];
    for (const [artikel_id, z] of welt.zustaende) {
        if (z.lagerort_id === null || !teilbaum.has(z.lagerort_id))
            continue;
        if (z.archiviert || z.verbleib === "nicht_erfasst")
            continue;
        const stammplatz = welt.stammplaetze.get(artikel_id) ?? null;
        if (stammplatz === null)
            ohneStammplatz.push(artikel_id);
        else if (!teilbaum.has(stammplatz))
            fremd.push(artikel_id);
    }
    return {
        zeilen: zeilen.map(({ kette: _kette, ...zeile }) => zeile),
        liegenLassen: liegen
            .sort((a, b) => (a.stammplatz < b.stammplatz ? -1 : a.stammplatz > b.stammplatz ? 1 : 0) ||
            (a.artikel_id < b.artikel_id ? -1 : 1))
            .map((l) => l.artikel_id),
        verliehen,
        archiviert,
        fremd,
        ohneStammplatz,
    };
}
/**
 * Welche Zeilen gewaehlt sind.
 *
 * `wahl` haelt, was jemand ausdruecklich an- oder abgewaehlt hat. Alle anderen
 * Zeilen folgen der Kiste, an der sie haengen, und ohne Kiste sind sie
 * gewaehlt. Wird GB-01 abgewaehlt, weil die Kiste noch draussen ist, geht damit
 * auch nichts „zurueck in GB-01“ (so vom Nutzer entschieden, ADR 0015).
 */
export function allesDaAuswahl(plan, wahl) {
    const zeilen = new Map(plan.zeilen.map((z) => [z.artikel_id, z]));
    const bekannt = new Map();
    const gewaehlt = (artikel_id, tiefe) => {
        const schon = bekannt.get(artikel_id);
        if (schon !== undefined)
            return schon;
        const ausdruecklich = wahl.get(artikel_id);
        const kiste = zeilen.get(artikel_id)?.haengt_an ?? null;
        const ergebnis = ausdruecklich ??
            (kiste === null || tiefe > KETTEN_GRENZE ? true : gewaehlt(kiste, tiefe + 1));
        bekannt.set(artikel_id, ergebnis);
        return ergebnis;
    };
    return new Set(plan.zeilen.filter((z) => gewaehlt(z.artikel_id, 0)).map((z) => z.artikel_id));
}
/**
 * Eine Zeile an- oder abwaehlen. Was an ihr haengt, folgt ihr wieder -- auch
 * wenn es vorher einzeln umgeschaltet war.
 */
export function allesDaUmschalten(plan, wahl, artikel_id) {
    const jetzt = allesDaAuswahl(plan, wahl).has(artikel_id);
    const neu = new Map(wahl);
    neu.set(artikel_id, !jetzt);
    const darunter = new Set([artikel_id]);
    let gewachsen = true;
    while (gewachsen) {
        gewachsen = false;
        for (const z of plan.zeilen) {
            if (z.haengt_an !== null && darunter.has(z.haengt_an) && !darunter.has(z.artikel_id)) {
                darunter.add(z.artikel_id);
                neu.delete(z.artikel_id);
                gewachsen = true;
            }
        }
    }
    return neu;
}
const NIE_GEBUCHT = {
    verbleib: "nicht_erfasst",
    lagerort_id: null,
    einsatzstelle_id: null,
    archiviert: false,
};
function verbleibNach(art) {
    switch (art) {
        case "einsatz":
        case "entnehmen":
            return "im_einsatz";
        case "ausgabe":
            return "ausgeliehen";
        default:
            return "im_lager";
    }
}
/**
 * Die Buchungen fuer die gewaehlten Zeilen, in ihrer Reihenfolge.
 *
 * Jede Zeile wird mit `verschiebenNach` gebucht -- derselben Regel wie
 * Verschieben und Einbuchen. Dabei zaehlt, was die Zeilen davor schon bewegt
 * haben: Kommt GB-01 zurueck auf den LKW, geht der Leitungsroller danach in
 * eine Kiste, die im Lager steht (`umlagerung`/`einbuchen`), nicht in eine
 * Kiste im Einsatz. Was in einer Kiste steckt, geht mit ihr, so wie der Server
 * es ableitet (ADR 0012).
 *
 * Wuerde eine Zeile ein Packstueck in sich selbst stecken, faellt sie heraus
 * (`verboten`) -- das kann nach widerstreitenden Offline-Buchungen vorkommen.
 */
export function allesDaBewegungen(plan, gewaehlt, welt) {
    const lage = new Map(welt.zustaende);
    const lageVon = (id) => lage.get(id);
    const ortVon = new Map();
    for (const o of welt.orte.values())
        if (o.artikel_id !== null)
            ortVon.set(o.artikel_id, o.id);
    /** Der Inhalt eines bewegten Packstuecks geht mit, verschachtelt. */
    const mitnehmen = (artikel_id, neu, tiefe) => {
        const ort = ortVon.get(artikel_id);
        if (ort === undefined || tiefe >= MAX_PACKSTUECK_TIEFE)
            return;
        for (const [id, z] of lage) {
            if (id === artikel_id || z.lagerort_id !== ort || z.archiviert)
                continue;
            const mit = {
                ...z,
                verbleib: neu.verbleib,
                einsatzstelle_id: neu.einsatzstelle_id,
            };
            lage.set(id, mit);
            mitnehmen(id, mit, tiefe + 1);
        }
    };
    const buchungen = [];
    const verboten = [];
    for (const zeile of plan.zeilen) {
        if (!gewaehlt.has(zeile.artikel_id))
            continue;
        const ziel = { art: "lagerort", id: zeile.stammplatz_id };
        if (zielVerboten(zeile.artikel_id, ziel, welt.orte, lageVon)) {
            verboten.push(zeile.artikel_id);
            continue;
        }
        const kiste = welt.orte.get(zeile.stammplatz_id)?.artikel_id ?? null;
        const bewegung = verschiebenNach(lage.get(zeile.artikel_id) ?? NIE_GEBUCHT, ziel, kiste === null ? null : (lage.get(kiste) ?? null));
        buchungen.push({ artikel_id: zeile.artikel_id, stammplatz_id: zeile.stammplatz_id, bewegung });
        const neu = {
            verbleib: verbleibNach(bewegung.art),
            lagerort_id: bewegung.ziel_lagerort_id,
            einsatzstelle_id: bewegung.einsatzstelle_id,
            archiviert: false,
        };
        lage.set(zeile.artikel_id, neu);
        mitnehmen(zeile.artikel_id, neu, 0);
    }
    return { buchungen, verboten };
}
//# sourceMappingURL=stammplatz.js.map